/* ============================================================
   BACKGAMMON KINGS — global behaviour (vanilla web components)
   ============================================================ */
(function () {
  'use strict';

  const root = document.documentElement;
  const moneyFormat = (window.Shopify && window.Shopify.currency) ? null : null;

  /* ---------- helpers ---------- */
  function formatMoney(cents) {
    const amount = (cents / 100);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: (window.BGK && window.BGK.currency) || 'USD'
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  async function fetchJSON(url, options) {
    const res = await fetch(url, Object.assign({ headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }, options));
    return res.json();
  }

  function publish(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }

  /* ---------- scroll reveal ---------- */
  const revealEls = () => document.querySelectorAll('.reveal:not(.is-visible)');
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    const observeAll = () => revealEls().forEach((el) => io.observe(el));
    document.addEventListener('DOMContentLoaded', observeAll);
    document.addEventListener('shopify:section:load', observeAll);
    observeAll();
  } else {
    revealEls().forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- sticky header state ---------- */
  const header = document.querySelector('.site-header');
  if (header) {
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 40);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- generic toggle drawers (menu / search) ---------- */
  class DrawerToggle extends HTMLElement {
    connectedCallback() {
      if (this.dataset.drawerToggleReady === 'true') return;
      this.dataset.drawerToggleReady = 'true';
      this.targetSel = this.getAttribute('data-target');
      this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.addEventListener('click', () => this.toggle());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle();
        }
      });
    }
    toggle() {
      const target = document.querySelector(this.targetSel);
      if (!target) return;
      const open = target.classList.toggle('is-open');
      document.body.classList.toggle('overflow-hidden', open || document.querySelector('.drawer.is-open, .cart-drawer.is-open'));
      this.setAttribute('aria-expanded', open);
      if (open) {
        target.removeAttribute('inert');
        const focusable = target.querySelector('button, a, input');
        if (focusable) focusable.focus();
      } else {
        target.setAttribute('inert', '');
      }
    }
  }
  customElements.define('drawer-toggle', DrawerToggle);

  // close buttons & overlay
  document.addEventListener('click', (e) => {
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('.drawer, .cart-drawer');
      if (drawer) { drawer.classList.remove('is-open'); drawer.setAttribute('inert',''); document.body.classList.remove('overflow-hidden'); }
    }
    if (e.target.classList.contains('drawer__overlay')) {
      const d = e.target.closest('.drawer, .cart-drawer');
      if (d) { d.classList.remove('is-open'); d.setAttribute('inert',''); document.body.classList.remove('overflow-hidden'); }
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.drawer.is-open, .cart-drawer.is-open').forEach((d) => { d.classList.remove('is-open'); d.setAttribute('inert',''); });
      document.body.classList.remove('overflow-hidden');
    }
  });

  /* ---------- quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = btn.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const val = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          this.input.value = val;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- checkout transition feedback ---------- */
  function resetCheckoutButtons() {
    document.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      delete button.dataset.checkoutSubmitting;
      if (button.dataset.originalLabel) button.textContent = button.dataset.originalLabel;
    });
  }

  document.addEventListener('submit', (e) => {
    const button = e.submitter;
    if (!button || button.name !== 'checkout') return;
    if (button.dataset.checkoutSubmitting === 'true') {
      e.preventDefault();
      return;
    }
    if (!button.dataset.originalLabel) button.dataset.originalLabel = button.textContent.trim();
    button.dataset.checkoutSubmitting = 'true';
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Redirecting…';
  });
  window.addEventListener('pageshow', resetCheckoutButtons);
  resetCheckoutButtons();

  /* ---------- cart store ---------- */
  const CartAPI = {
    async get() { return fetchJSON('/cart.js'); },
    async add(formData) {
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: formData });
      if (!res.ok) { const err = await res.json(); throw err; }
      return res.json();
    },
    async change(line, quantity) {
      return fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ line, quantity }) });
    }
  };
  window.BGKCart = CartAPI;

  function updateCartCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.classList.toggle('is-empty', cart.item_count === 0);
    });
    publish('cart:updated', { cart });
  }

  /* ---------- cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.el = document.getElementById('CartDrawer');
      document.addEventListener('cart:render', (e) => this.renderHTML(e.detail.sections));
      this.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-line-remove]');
        if (removeBtn) { e.preventDefault(); this.changeLine(removeBtn.dataset.lineRemove, 0); }
      });
      this.addEventListener('change', (e) => {
        const qty = e.target.closest('[data-line-qty]');
        if (qty) this.changeLine(qty.dataset.lineQty, parseInt(e.target.value, 10));
      });
    }
    open() {
      this.el.classList.add('is-open'); this.el.removeAttribute('inert');
      document.body.classList.add('overflow-hidden');
    }
    async changeLine(line, quantity) {
      this.el.classList.add('is-loading');
      const cart = await CartAPI.change(parseInt(line, 10), quantity);
      updateCartCount(cart);
      await this.refresh();
      this.el.classList.remove('is-loading');
    }
    async refresh() {
      const res = await fetch('/?sections=cart-drawer');
      const data = await res.json();
      if (data['cart-drawer']) {
        const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
        const fresh = html.querySelector('#CartDrawer .cart-drawer__inner');
        const current = this.el.querySelector('.cart-drawer__inner');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ---------- product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-disabled', 'true');
      if (this.errorEl) this.errorEl.hidden = true;
      try {
        const cart = await CartAPI.add(new FormData(this.form));
        const fresh = await CartAPI.get();
        updateCartCount(fresh);
        const drawer = document.querySelector('cart-drawer');
        if (drawer) { await drawer.refresh(); drawer.open(); }
        else { window.location = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart'; }
      } catch (err) {
        if (this.errorEl) { this.errorEl.textContent = (err && err.description) || 'Could not add to cart.'; this.errorEl.hidden = false; }
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- variant selector ---------- */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      return Array.from(this.querySelectorAll('select, input:checked')).map((i) => i.value);
    }
    onChange() {
      const opts = this.selectedOptions();
      const match = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      const root = this.closest('[data-product-root]') || document;
      const idInput = root.querySelector('[name="id"]');
      const priceEl = root.querySelector('[data-price]');
      const btn = root.querySelector('[type="submit"]');
      if (match) {
        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.innerHTML = formatMoney(match.price);
        if (btn) {
          btn.toggleAttribute('disabled', !match.available);
          const txt = btn.querySelector('[data-add-text]');
          if (txt) txt.textContent = match.available ? btn.dataset.addText : btn.dataset.soldText;
        }
        const url = new URL(window.location);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /* ---------- product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.querySelectorAll('[data-gallery-thumb]').forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          const id = thumb.dataset.galleryThumb;
          this.main.querySelectorAll('[data-media]').forEach((m) => m.classList.toggle('is-active', m.dataset.media === id));
        });
      });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- newsletter (uses contact form, just UX) ---------- */
  // Shopify emits the same default form id for contact and customer forms.
  // Keep the contact form id stable and make the footer newsletter unique.
  document.querySelectorAll('.footer-newsletter#contact_form').forEach((form, index) => {
    form.id = index === 0 ? 'FooterNewsletterForm' : `FooterNewsletterForm-${index + 1}`;
  });

})();

// Remove the obsolete engraving paragraph from the gift guide only.
(() => {
  if (window.location.pathname !== '/blogs/news/backgammon-gift-guide') return;

  const obsoleteEngravingCopy = 'Selected sets may offer engraving or other personalization. Make sure names, dates and spelling are final before submitting personalized details.';

  const removeObsoleteEngravingCopy = () => {
    const articleContent = document.querySelector('.article__content');
    if (!articleContent) return;

    articleContent.querySelectorAll('p').forEach((paragraph) => {
      if (paragraph.textContent.trim() === obsoleteEngravingCopy) paragraph.remove();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeObsoleteEngravingCopy, { once: true });
  } else {
    removeObsoleteEngravingCopy();
  }
})();
